import React, { useState, useEffect, useCallback } from 'react';
import { tagText } from '../lib/assets';
import { useNavigate } from 'react-router-dom';
import BulkApprovalActions from '../components/BulkApprovalActions';
import HoursApprovalQueue from '../components/HoursApprovalQueue';
import ReopenQueue from '../components/ReopenQueue';
import TicketApprovalDetail from '../components/TicketApprovalDetail';
import RequesterName from '../components/RequesterName';
import { fmtDate } from '../lib/when';


/** 2 -> "2nd", 3 -> "3rd", 11 -> "11th". */
const ordinal = (n) => {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
  return `${n}${suffix}`;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ApprovalsPage = () => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedContractor, setSelectedContractor] = useState('');
  // Optional. The hours authorised for this job, carried onto the work order
  // when it is raised and shown back to whoever signs the time off.
  const [budgetHours, setBudgetHours] = useState('');
  // The ticket whose detail is open, and whether this person is allowed the
  // ticket-approval queue at all.
  const [reviewing, setReviewing] = useState(null);
  const [ticketQueueDenied, setTicketQueueDenied] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // The rejection box only exists once Reject has been pressed. It used to sit
  // open under an "or" divider on every card being reviewed, which made the
  // panel twice as tall as the decision it was asking for.
  const [rejecting, setRejecting] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  // What the last decision did, said on the page. These were alert() calls -
  // a modal to dismiss after every accept, and the page's own error banner
  // ignored while a second copy of the message sat in a dialog.
  const [notice, setNotice] = useState(null);
  // Sent back to the requester for more information (087): waiting on THEM,
  // so under a tab of its own rather than in the queue it is not waiting in.
  const [sentBack, setSentBack] = useState([]);
  const [tab, setTab] = useState('decide');          // 'decide' | 'requester'
  const [askingBack, setAskingBack] = useState(false);
  const [backQuestion, setBackQuestion] = useState('');
  const navigate = useNavigate();

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const headers = {};
      
      const response = await fetch(`${API_URL}/approvals`, { headers });
      // Approving a ticket to be worked on is administrative; signing off hours
      // is not. An approver who is not an administrator is entitled to the
      // queue below and not to this one, so a refusal here hides this section
      // rather than covering the page in an error about something that was
      // never theirs.
      if (response.status === 401 || response.status === 403) {
        setTicketQueueDenied(true);
        setApprovals([]);
        setError(null);
        return;
      }
      if (!response.ok) throw new Error('Failed to fetch approvals');

      const data = await response.json();
      setApprovals(Array.isArray(data) ? data : []);
      setTicketQueueDenied(false);
      setError(null);
      // The requester's turn. Only rows that say they were sent back count: a
      // server from before this answers ?view= with the ordinary queue.
      try {
        const back = await fetch(`${API_URL}/approvals?view=sent_back`, { headers });
        const rows = back.ok ? await back.json() : [];
        setSentBack(Array.isArray(rows) ? rows.filter((t) => t.approval_status === 'sent_back') : []);
      } catch {
        setSentBack([]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching approvals:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchContractors = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/contractors`);
      if (!response.ok) throw new Error('Failed to fetch contractors');
      
      const data = await response.json();
      setContractors(data);
    } catch (err) {
      console.error('Error fetching contractors:', err);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
    fetchContractors();
  }, [fetchApprovals, fetchContractors]);

  const handleAccept = async (ticketId) => {
    if (!selectedContractor) {
      setError('Please select a contractor');
      return;
    }

    try {
      setActionInProgress(ticketId);
      setError(null);
      setNotice(null);
      const headers = { 'Content-Type': 'application/json' };
      
      const response = await fetch(`${API_URL}/approvals/${ticketId}/accept`, {
        method: 'PUT',
        headers,
        // Blank means no budget, which is not the same as a budget of zero -
        // the server refuses zero for exactly that reason.
        body: JSON.stringify({
          contractor_id: selectedContractor,
          allocated_hours: budgetHours.trim() === '' ? null : budgetHours.trim()
        })
      });

      if (!response.ok) {
        const failed = await response.json().catch(() => ({}));
        throw new Error(failed.error || 'Failed to accept ticket');
      }

      // A budget typed against a server whose migration has not been run yet
      // would otherwise disappear without a word.
      const saved = await response.json().catch(() => ({}));

      // Refresh the approvals list
      await fetchApprovals();
      setSelectedTicket(null);
      setSelectedContractor('');
      setBudgetHours('');

      // Show success message
      setNotice(saved.warning
        // Decided for an approver who is away (113): said, so nobody wonders
        // whose decision it was.
        || (saved.on_behalf_of ? `Approved on behalf of ${saved.on_behalf_of.name}` : null)
        || (budgetHours.trim()
          ? `Ticket accepted and assigned, with a budget of ${budgetHours.trim()}h`
          : 'Ticket accepted and assigned to contractor'));
    } catch (err) {
      setError('Error accepting ticket: ' + err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReject = async (ticketId) => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      setActionInProgress(ticketId);
      setError(null);
      setNotice(null);
      const headers = { 'Content-Type': 'application/json' };
      
      const response = await fetch(`${API_URL}/approvals/${ticketId}/reject`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ reason: rejectReason })
      });

      if (!response.ok) throw new Error('Failed to reject ticket');
      const rejected = await response.json().catch(() => ({}));

      // Refresh the approvals list
      await fetchApprovals();
      setSelectedTicket(null);
      setRejectReason('');
      setRejecting(false);

      // Show success message
      setNotice(rejected.on_behalf_of ? `Rejected on behalf of ${rejected.on_behalf_of.name}` : 'Ticket rejected');
    } catch (err) {
      setError('Error rejecting ticket: ' + err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  /** "Sent back for more information" (087): the question is required, and the requester is shown it. */
  const handleSendBack = async (ticketId) => {
    if (!backQuestion.trim()) return;
    try {
      setActionInProgress(ticketId);
      setError(null);
      setNotice(null);
      const headers = { 'Content-Type': 'application/json' };
      const response = await fetch(`${API_URL}/approvals/${ticketId}/send-back`, {
        method: 'PUT', headers, body: JSON.stringify({ question: backQuestion.trim() })
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'the server refused it');
      await fetchApprovals();
      setSelectedTicket(null);
      setAskingBack(false);
      setBackQuestion('');
      setNotice(body.legacy ? 'Asked. It stays waiting for your decision.'
        : 'Sent back for more information. It is under "Waiting for requester" until they answer.');
    } catch (err) {
      setError('Error sending it back: ' + err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  // `data = {}` because the crash this page reported -
  //   "cannot read properties of undefined (reading 'contractorId')"
  // - was the line below reading it off an argument that never arrived. The
  // caller is fixed; the default means a future one that forgets a field sends
  // a request without a contractor rather than throwing with nothing useful
  // in it.
  const handleBulkApprovalAction = async (action, ticketIds, data = {}) => {
    const ids = Array.isArray(ticketIds) ? ticketIds : [];
    if (ids.length === 0) {
      setError('Select at least one ticket first.');
      return;
    }
    try {
      setError(null);
      setNotice(null);
      const endpoint = action === 'approve'
        ? '/approvals/bulk/accept'
        : '/approvals/bulk/reject';

      const headers = { 'Content-Type': 'application/json' };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          ticket_ids: ids,
          contractor_id: data.contractorId || null,
          rejection_reason: data.rejectionReason || null
        })
      });

      // The server says useful things here - which tickets have not been
      // approved yet, that a contractor is inactive - and 'Action failed'
      // threw every one of them away.
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || `the server refused it (${response.status})`);
      }

      // Refresh approvals list
      await fetchApprovals();
      setNotice(`Successfully ${action === 'approve' ? 'approved' : 'rejected'} ${ids.length} ticket(s)`);
    } catch (error) {
      console.error('Bulk action failed:', error);
      setError('Error processing bulk action: ' + error.message);
    }
  };


  if (loading) {
    return (
      <div className="approvals-page">
        <div className="container">
          <div className="loading">Loading approvals...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="approvals-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Approvals</h1>
          <button className="btn btn-secondary" onClick={() => navigate('/manage')}>
            Back to Dashboard
          </button>
        </div>

        {error && <div className="error-message" role="alert">{error}</div>}
        {notice && <div className="success-message" role="status">{notice}</div>}

        {/* Two different approvals, kept visibly apart. This one is after the
            work: a contractor has finished and submitted their hours, and the
            ticket is frozen until it is signed off or sent back. The queue
            below is the older question - should this ticket be worked on at
            all, and by whom. */}
        {/* Above the hours queue on purpose: a reopen request is about work
            that has already been signed off and possibly invoiced, so it is
            both rarer and more urgent than the day's sign-offs. It hides
            itself when there is nothing waiting. */}
        <ReopenQueue />

        <HoursApprovalQueue />

        {/* Hidden rather than shown empty for somebody it was never for: a
            "0 Pending Approvals" card reads as "nothing to do", which is a
            different and wrong answer to "this queue is not yours". */}

        {/* Two queues for the one question, by whose move it is: the
            approver's, or - sent back for more information - the
            requester's. */}
        {!ticketQueueDenied && <h2 className="approvals-h">Tickets waiting for approval</h2>}
        {!ticketQueueDenied && (
          <div className="approvals-tabs" role="tablist" aria-label="Ticket approvals">
            {[['decide', 'Waiting for a decision', approvals.length],
              ['requester', 'Waiting for requester', sentBack.length]].map(([k, label, n]) => (
              <button key={k} type="button" role="tab" id={`approvals-tab-${k}`} aria-selected={tab === k}
                      aria-controls={`approvals-panel-${k}`} className={`approvals-tab${tab === k ? ' is-on' : ''}`}
                      data-tab={k} onClick={() => setTab(k)}>
                {label}<span className="approvals-tab-n">{n}</span>
              </button>
            ))}
          </div>
        )}

        {!ticketQueueDenied && tab === 'requester' && (
          <div role="tabpanel" id="approvals-panel-requester" aria-labelledby="approvals-tab-requester">
            {sentBack.length === 0 ? (
              <div className="empty-state"><p>Nothing is waiting on a requester</p></div>
            ) : (
              <div className="approvals-list">
                {sentBack.map((ticket) => (
                  <div key={ticket.id} className="approval-card is-sent-back" data-sent-back={ticket.id}>
                    <div className="card-header">
                      <div className="ticket-info">
                        <h3 className="ticket-title">
                          <span className="ticket-number">{ticket.ticket_number}</span>
                          {ticket.title}
                        </h3>
                        <p className="requester-info">
                          Waiting for <strong><RequesterName ticket={ticket} fallback="the requester" /></strong>
                          {ticket.sendback?.asked_at ? ` since ${new Date(ticket.sendback.asked_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                        </p>
                      </div>
                      <div className="priority-badge" data-priority={ticket.priority_name?.toLowerCase()}>
                        {ticket.priority_name}
                      </div>
                    </div>
                    {ticket.sendback?.question && (
                      <div className="card-body">
                        <p className="sent-back-asked">
                          {ticket.sendback.asked_by_name || 'An approver'} asked:
                        </p>
                        <blockquote className="sent-back-question">{ticket.sendback.question}</blockquote>
                        <p className="form-hint">The SLA clock is paused until they answer. It comes back to the queue by itself.</p>
                      </div>
                    )}
                    <div className="card-footer">
                      <button className="btn btn-tertiary" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                        Open the ticket
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {ticketQueueDenied || tab !== 'decide' ? null : approvals.length === 0 ? (
          <div className="empty-state">
            <p>No tickets awaiting approval</p>
          </div>
        ) : (
          <>
            {/* Bulk Approval Actions - full width, above the queue */}
            <BulkApprovalActions tickets={approvals} onAction={handleBulkApprovalAction} />

            <div className="approvals-list">
            {approvals.map(ticket => (
              <div key={ticket.id} className="approval-card">
                <div className="card-header">
                  <div className="ticket-info">
                    <h3 className="ticket-title">
                      <span className="ticket-number">{ticket.ticket_number}</span>
                      {ticket.title}
                    </h3>
                    {/* Everything from the requester down to Created is the
                        detail you need once you are deciding, not while you
                        are scanning. Folding it away until Review & Decide is
                        pressed roughly triples how many tickets fit on a
                        screen, which is the actual complaint about this page. */}
                    {selectedTicket === ticket.id && (
                      <p className="requester-info">
                        From: <strong><RequesterName ticket={ticket} fallback="" /></strong>{ticket.requester_email ? ` (${ticket.requester_email})` : ''}
                      </p>
                    )}
                  </div>
                  <div className="priority-badge" data-priority={ticket.priority_name?.toLowerCase()}>
                    {ticket.priority_name}
                  </div>
                </div>

                {/* Named to an approver who is away (113): who, until when,
                    and - when it is the person looking - that they are the
                    one covering, so the decision is recorded on the
                    approver's behalf. */}
                {ticket.away && (
                  <p className={`approval-away${ticket.covering ? ' is-mine' : ''}`} data-away={ticket.id}>
                    {ticket.covering
                      ? <>For <strong>{ticket.approver_name}</strong>, away until {fmtDate(ticket.away.until)} &mdash; you are covering. Your decision is recorded on their behalf.</>
                      : <>{ticket.approver_name} is away until {fmtDate(ticket.away.until)}{ticket.away.delegate_name ? ` — ${ticket.away.delegate_name} is covering` : ''}.</>}
                  </p>
                )}

                {/* Back from the requester with an answer (087): the first
                    thing to read, open or not. */}
                {ticket.sendback && ticket.sendback.answered_at && (
                  <div className="sent-back-answer" data-answered={ticket.id}>
                    <p className="sent-back-asked">
                      <strong>{ticket.sendback.answered_by_name || 'The requester'} answered</strong>
                      {' '}“{ticket.sendback.question}”
                    </p>
                    <blockquote className="sent-back-question is-answer">{ticket.sendback.answer}</blockquote>
                  </div>
                )}

                {selectedTicket === ticket.id && (
                <div className="card-body">
                  <p className="description">{ticket.description}</p>

                  <div className="ticket-meta">
                    {ticket.department_name && (
                      <div className="meta-item">
                        <span className="meta-label">Department:</span>
                        <span className="meta-value">{ticket.department_name}</span>
                      </div>
                    )}
                    {ticket.category_name && (
                      <div className="meta-item">
                        <span className="meta-label">Category:</span>
                        <span className="meta-value">{ticket.category_name}</span>
                      </div>
                    )}
                    <div className="meta-item">
                      <span className="meta-label">Created:</span>
                      <span className="meta-value">{fmtDate(ticket.created_at)}</span>
                    </div>
                    {/* Which machine, and how often it has needed fixing -
                        "the third ticket on this laptop this year" is the
                        thing to know before agreeing more hours on it. */}
                    {ticket.device && (
                      <div className="meta-item">
                        <span className="meta-label">Device:</span>
                        <span className="meta-value">
                          {tagText(ticket.device)}
                          {[ticket.device.manufacturer, ticket.device.model].filter(Boolean).length
                            ? ` · ${[ticket.device.manufacturer, ticket.device.model].filter(Boolean).join(' ')}` : ''}
                          {ticket.device.tickets_this_year > 1
                            ? ` · ${ordinal(ticket.device.tickets_this_year)} ticket on it in 12 months` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {selectedTicket === ticket.id ? (
                  <div className="card-actions">
                    <div className="action-section">
                      <label className="form-label">Select Contractor to Assign:</label>
                      <select
                        value={selectedContractor}
                        onChange={(e) => setSelectedContractor(e.target.value)}
                        className="form-select"
                        disabled={actionInProgress === ticket.id}
                      >
                        <option value="">-- Choose a contractor --</option>
                        {contractors.map(contractor => (
                          <option key={contractor.id} value={contractor.id}>
                            {contractor.name} ({contractor.email})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="action-section">
                      <label className="form-label" htmlFor={`budget-${ticket.id}`}>
                        Hour budget (optional):
                      </label>
                      <input
                        id={`budget-${ticket.id}`}
                        /* text with inputMode rather than type=number: a number
                           input silently discards what it cannot parse, so a
                           mistyped budget becomes no budget with nothing on
                           screen to say so. */
                        type="text"
                        inputMode="decimal"
                        className="form-input"
                        value={budgetHours}
                        onChange={(e) => setBudgetHours(e.target.value.replace(/[^0-9.]/g, ''))}
                        placeholder="e.g. 8"
                        disabled={actionInProgress === ticket.id}
                      />
                      <p className="form-hint">
                        Leave blank if this job has no agreed limit. If you set one,
                        the contractor sees it while logging time and you&rsquo;ll be
                        shown any over-run when you sign the hours off &mdash; it
                        doesn&rsquo;t stop them working.
                      </p>
                    </div>

                    {/* One row, three decisions. Accept and Reject sat in two
                        separate rows with an "or" divider and a permanently
                        open reason box between them, so the two halves of one
                        choice never appeared side by side. */}
                    <div className="action-buttons">
                      <button
                        className="btn btn-primary"
                        onClick={() => handleAccept(ticket.id)}
                        disabled={actionInProgress === ticket.id || !selectedContractor || rejecting || askingBack}
                      >
                        {actionInProgress === ticket.id ? 'Processing...' : 'Accept & Assign'}
                      </button>
                      <button
                        className="btn btn-secondary"
                        data-action="send-back"
                        onClick={() => {
                          if (askingBack) { handleSendBack(ticket.id); return; }
                          setAskingBack(true);
                          setRejecting(false);
                        }}
                        disabled={actionInProgress === ticket.id || (askingBack && !backQuestion.trim())}
                      >
                        {askingBack ? 'Send it back' : 'Send back…'}
                      </button>
                      <button
                        className="btn btn-tertiary"
                        onClick={() => {
                          setSelectedTicket(null);
                          setSelectedContractor('');
                          setBudgetHours('');
                          setRejecting(false);
                          setRejectReason('');
                          setAskingBack(false);
                          setBackQuestion('');
                        }}
                        disabled={actionInProgress === ticket.id}
                      >
                        Cancel
                      </button>
                      {/* The first press opens the reason box; the second,
                          with a reason in it, actually rejects. */}
                      <button
                        className="btn btn-danger"
                        onClick={() => {
                          if (rejecting) { handleReject(ticket.id); return; }
                          setRejecting(true);
                          setAskingBack(false);
                        }}
                        disabled={actionInProgress === ticket.id
                          || (rejecting && !rejectReason.trim())}
                      >
                        {actionInProgress === ticket.id ? 'Processing...' : 'Reject'}
                      </button>
                    </div>

                    {askingBack && (
                      <div className="action-section is-send-back">
                        <label className="form-label" htmlFor={`back-${ticket.id}`}>
                          What do you need from them?
                        </label>
                        <textarea
                          id={`back-${ticket.id}`}
                          value={backQuestion}
                          onChange={(e) => setBackQuestion(e.target.value)}
                          className="form-textarea"
                          placeholder="e.g. Which office is the printer in, and is it the colour one?"
                          disabled={actionInProgress === ticket.id}
                          rows="4"
                          autoFocus
                        />
                        <p className="form-hint">
                          They are shown this word for word and asked to reply. It moves to
                          &ldquo;Waiting for requester&rdquo; until they do, and the SLA clock stops meanwhile.
                        </p>
                      </div>
                    )}

                    {rejecting && (
                      <div className="action-section is-rejection">
                        <label className="form-label" htmlFor={`reject-${ticket.id}`}>
                          Why is this being rejected?
                        </label>
                        <textarea
                          id={`reject-${ticket.id}`}
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          className="form-textarea"
                          placeholder="Explain why you're rejecting this ticket..."
                          disabled={actionInProgress === ticket.id}
                          rows="5"
                          autoFocus
                        />
                        <p className="form-hint">
                          The person who raised the ticket is shown this, so say
                          enough for them to know what to do next.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="card-footer">
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setSelectedTicket(ticket.id);
                        // The panel is shared between cards, so a reason typed
                        // against one ticket must not follow you to the next.
                        setRejecting(false);
                        setRejectReason('');
                        setAskingBack(false);
                        setBackQuestion('');
                        setSelectedContractor('');
                        setBudgetHours('');
                      }}
                    >
                      Review &amp; Decide
                    </button>
                    {/* Opens the ticket itself - the conversation on it, its
                        history - for the times the card is not enough to
                        decide from. */}
                    <button
                      className="btn btn-tertiary"
                      onClick={() => setReviewing(ticket)}
                    >
                      Open the ticket
                    </button>
                  </div>
                )}
              </div>
            ))}
            </div>
          </>
        )}

        {reviewing && (
          <TicketApprovalDetail ticket={reviewing} onClose={() => setReviewing(null)} />
        )}
      </div>
    </div>
  );
};

export default ApprovalsPage;
